// src/resources/Vehicles/vehicleFinance.js
// Single source of truth for remaining finance, deferments, settlement, and vehicle status.

export const MONTH_CHOICES = [
  { id: 1, name: "January" },
  { id: 2, name: "February" },
  { id: 3, name: "March" },
  { id: 4, name: "April" },
  { id: 5, name: "May" },
  { id: 6, name: "June" },
  { id: 7, name: "July" },
  { id: 8, name: "August" },
  { id: 9, name: "September" },
  { id: 10, name: "October" },
  { id: 11, name: "November" },
  { id: 12, name: "December" },
];

export const normalizeDate = (d) => {
  if (!d) return null;
  if (typeof d === "object" && typeof d.toDate === "function") {
    const dt = d.toDate();
    return isNaN(dt.getTime()) ? null : dt;
  }
  if (typeof d === "object" && typeof d.seconds === "number") {
    const dt = new Date(d.seconds * 1000);
    return isNaN(dt.getTime()) ? null : dt;
  }
  if (d instanceof Date) return isNaN(d.getTime()) ? null : d;
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? null : dt;
};

export const startOfDay = (dt) => {
  if (!dt) return null;
  const d = new Date(dt);
  d.setHours(0, 0, 0, 0);
  return d;
};

const monthKey = (year, month) => `${Number(year)}-${String(Number(month)).padStart(2, "0")}`;

const dateMonthKey = (date) => monthKey(date.getFullYear(), date.getMonth() + 1);

const compareMonthKeys = (a, b) => a.localeCompare(b);

export const monthName = (month) => {
  const m = MONTH_CHOICES.find((x) => Number(x.id) === Number(month));
  return m ? m.name : "";
};

export const addMonths = (date, months) => {
  const base = normalizeDate(date);
  if (!base) return null;

  const d = new Date(base);
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + (Number(months) || 0));

  // Keep the original day where possible, otherwise use last day of target month.
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, lastDay));
  return d;
};

export const cleanDeferredInstallments = (items = [], fallbackAmount = 0) => {
  if (!Array.isArray(items)) return [];

  const seen = new Set();
  return items
    .map((item) => {
      const month = Number(item?.month);
      const year = Number(item?.year);
      if (!month || !year || month < 1 || month > 12) return null;

      const key = monthKey(year, month);
      if (seen.has(key)) return null;
      seen.add(key);

      return {
        month,
        year,
        amount: Number(item?.amount) || Number(fallbackAmount) || 0,
        remarks: item?.remarks || "",
      };
    })
    .filter(Boolean)
    .sort((a, b) => compareMonthKeys(monthKey(a.year, a.month), monthKey(b.year, b.month)));
};

export const getDeferredInstallmentCount = (v, asOf = null) => {
  const monthly = Number(v?.monthlyInstallment) || 0;
  const deferred = cleanDeferredInstallments(v?.deferredInstallments, monthly);
  if (!asOf) return deferred.length;

  const asOfDate = normalizeDate(asOf);
  if (!asOfDate) return deferred.length;

  const asOfKey = dateMonthKey(asOfDate);
  return deferred.filter((x) => compareMonthKeys(monthKey(x.year, x.month), asOfKey) <= 0).length;
};

export const getDeferredInstallmentsTotal = (v, asOf = null) => {
  const monthly = Number(v?.monthlyInstallment) || 0;
  const deferred = cleanDeferredInstallments(v?.deferredInstallments, monthly);
  if (!asOf) return deferred.reduce((sum, x) => sum + (Number(x.amount) || monthly), 0);

  const asOfDate = normalizeDate(asOf);
  if (!asOfDate) return deferred.reduce((sum, x) => sum + (Number(x.amount) || monthly), 0);

  const asOfKey = dateMonthKey(asOfDate);
  return deferred
    .filter((x) => compareMonthKeys(monthKey(x.year, x.month), asOfKey) <= 0)
    .reduce((sum, x) => sum + (Number(x.amount) || monthly), 0);
};

export const formatDeferredInstallments = (v) => {
  const monthly = Number(v?.monthlyInstallment) || 0;
  const deferred = cleanDeferredInstallments(v?.deferredInstallments, monthly);
  if (!deferred.length) return "";
  return deferred.map((x) => `${monthName(x.month)} ${x.year}`).join(", ");
};

const monthsDueInclusive = (start, asOf) => {
  if (!start || !asOf) return 0;
  if (asOf < start) return 0;

  let months =
    (asOf.getFullYear() - start.getFullYear()) * 12 +
    (asOf.getMonth() - start.getMonth());

  if (asOf.getDate() >= start.getDate()) months += 1;
  return Math.max(0, months);
};

const isSameMonthYear = (date, item) => {
  if (!date || !item) return false;
  return (
    date.getFullYear() === Number(item.year) &&
    date.getMonth() + 1 === Number(item.month)
  );
};

const buildInstallmentDates = (start, end) => {
  const dates = [];
  const s = normalizeDate(start);
  const e = normalizeDate(end);
  if (!s || !e || e < s) return dates;

  let current = new Date(s);
  let guard = 0;

  while (current <= e && guard < 300) {
    dates.push(new Date(current));
    current = addMonths(current, 1);
    guard += 1;
  }

  return dates;
};

export const getFinalInstallmentEnd = (v) => {
  const originalEnd = normalizeDate(v?.installmentEnd);
  const manualRevisedEnd = normalizeDate(v?.manualRevisedEndDate);
  if (manualRevisedEnd) return manualRevisedEnd;

  const deferredCount = getDeferredInstallmentCount(v);
  if (originalEnd && deferredCount > 0) return addMonths(originalEnd, deferredCount);

  return originalEnd;
};

export const isVehicleActiveAsOf = (v, asOf) => {
  const asOfDay = startOfDay(asOf || new Date());
  const createdAt = normalizeDate(v?.createdAt);
  const soldAt = normalizeDate(v?.soldAt);

  if (createdAt && startOfDay(createdAt) > asOfDay) return false;
  if (soldAt && startOfDay(soldAt) <= asOfDay) return false;

  return true;
};

export const calculateRemainingFinance = (v, asOf = new Date()) => {
  const total = Number(v?.totalFacilityAmount) || 0;
  const monthly = Number(v?.monthlyInstallment) || 0;
  const mortgage = (v?.mortgageFrom || "").toLowerCase();
  const bankCharges = Number(v?.bankCharges) || 0;

  if (mortgage === "cash") return 0;

  const asOfDate = normalizeDate(asOf) || new Date();
  const asOfDay = startOfDay(asOfDate);

  const soldAt = normalizeDate(v?.soldAt);
  if (soldAt && startOfDay(soldAt) <= asOfDay) return 0;

  const settlementDate = normalizeDate(v?.settlementDate);
  if (settlementDate && startOfDay(settlementDate) <= asOfDay) return 0;

  if (!monthly) return 0;

  const start = normalizeDate(v?.installmentStart);
  const originalEnd = normalizeDate(v?.installmentEnd);
  const finalEnd = getFinalInstallmentEnd(v);
  const deferred = cleanDeferredInstallments(v?.deferredInstallments, monthly);

  // If start/end dates are available, calculate by the ORIGINAL installment schedule.
  // Important rule:
  // - Future original installments are unpaid.
  // - Past original installments are assumed paid, EXCEPT months marked as deferred.
  // - Deferred months stay unpaid until they are removed from the deferred list or
  //   the vehicle is marked as settled.
  // - The auto revised end date is only for display/schedule reference; it must not
  //   reduce the remaining amount automatically, otherwise a deferred month can
  //   disappear after the shifted date passes.
  if (start && originalEnd) {
    const originalSchedule = buildInstallmentDates(start, originalEnd);

    const remainingMonths = originalSchedule.filter((dueDate) => {
      const isDeferred = deferred.some((item) => isSameMonthYear(dueDate, item));

      // Any selected deferred month is unpaid, even if its original due date
      // already passed.
      if (isDeferred) return true;

      // Normal non-deferred month is unpaid only if its due date is after
      // the report/as-of date.
      return startOfDay(dueDate) > asOfDay;
    }).length;

    const remaining = Number((remainingMonths * monthly + bankCharges).toFixed(2));

    const epsilon = Math.max(10, monthly * 0.02);
    if (remaining <= epsilon) return 0;
    return remaining > 0 ? remaining : 0;
  }

  // Fallback for incomplete records without proper end date: use old balance method.
  if (!total) return Number(bankCharges.toFixed(2));
  if (!start) return Number((total + bankCharges).toFixed(2));
  if (asOfDate < start) return Number((total + bankCharges).toFixed(2));

  let effectiveAsOf = new Date(asOfDate);
  if (finalEnd && effectiveAsOf > finalEnd) effectiveAsOf = finalEnd;

  let dueMonths = monthsDueInclusive(start, effectiveAsOf);
  const deferredUntilAsOf = getDeferredInstallmentCount(v, effectiveAsOf);
  let paidMonths = Math.max(0, dueMonths - deferredUntilAsOf);

  const maxPaidMonthsByAmount = Math.ceil(total / monthly);
  if (paidMonths > maxPaidMonthsByAmount) paidMonths = maxPaidMonthsByAmount;

  const paid = paidMonths * monthly;
  const remainingRaw = total - paid + bankCharges;
  const remaining = Number(remainingRaw.toFixed(2));

  const epsilon = Math.max(10, monthly * 0.02);
  if (remaining <= epsilon) return 0;
  return remaining > 0 ? remaining : 0;
};

export const vehicleStatusAsOf = (v, asOf = new Date()) => {
  const asOfDay = startOfDay(asOf || new Date());

  const soldAt = normalizeDate(v?.soldAt);
  if (soldAt && startOfDay(soldAt) <= asOfDay) return "SOLD";

  const mortgage = (v?.mortgageFrom || "").toLowerCase();
  if (mortgage === "cash") return "CASH";

  const settlementDate = normalizeDate(v?.settlementDate);
  if (settlementDate && startOfDay(settlementDate) <= asOfDay) return "SETTLED";

  const remaining = calculateRemainingFinance(v, asOf);
  if (remaining <= 0) return "PAID";

  if (getDeferredInstallmentCount(v, asOf) > 0) return "DEFERRED";

  return "FINANCED";
};
